<template>
  <section>
    <el-collapse v-model="isOpen">
      <el-collapse-item
        title="Search Filters"
        name="appointments-search-section"
        id="appointments-search-section"
      >
        <el-form
          id="appointments-search-section-form"
          class="search-section-form"
          :model="filters"
          label-position="top"
          size="mini"
        >
          <el-row>
            <el-col :span="24">
              <div class="responsive-filter-row">
                <!-- Account Autocomplete -->
                <div class="filter-col account-col">
                  <el-form-item :label="$t('customerCenter.appointments.account')">
                    <master-record-autocomplete
                      ref="accountAuto"
                      :url="accountUrl"
                      :model.sync="filters.accountId"
                      label-field="name"
                      value-field="id"
                      placeholder="Search Account"
                      label-field-last-with-dash="number"
                      class="width-100p"
                      :edit-value="accountNameToEdit"
                      @update:text="onAccountText"
                    />
                  </el-form-item>
                </div>

                <!-- Load Number -->
                <div class="filter-col load-col">
                  <el-form-item :label="$t('customerCenter.appointments.loadNumber')">
                    <el-input v-model="filters.loadNumber" placeholder="Load #" clearable></el-input>
                  </el-form-item>
                </div>

                <!-- Ship Date Range -->
                <div class="filter-col ship-col">
                  <el-form-item :label="$t('customerCenter.appointments.shipDate')">
                    <el-date-picker
                      v-model="filters.shipDateRange"
                      type="daterange"
                      range-separator="-"
                      start-placeholder="Start"
                      end-placeholder="End"
                      format="MM/dd/yyyy"
                      value-format="yyyy-MM-dd"
                      style="width: 100%;"
                    >
                    </el-date-picker>
                  </el-form-item>
                </div>

                <!-- Appointment Date Range -->
                <div class="filter-col appt-col">
                  <el-form-item :label="$t('customerCenter.appointments.appointmentDate')">
                    <el-date-picker
                      v-model="filters.appointmentDateRange"
                      type="daterange"
                      range-separator="-"
                      start-placeholder="Start"
                      end-placeholder="End"
                      format="MM/dd/yyyy"
                      value-format="yyyy-MM-dd"
                      style="width: 100%;"
                    >
                    </el-date-picker>
                  </el-form-item>
                </div>

                <!-- Hub -->
                <div class="filter-col hub-filter-col">
                  <el-form-item :label="$t('customerCenter.appointments.hub')">
                    <hub-select :empty="true" v-model="filters.hub"/>
                  </el-form-item>
                </div>
              </div>
            </el-col>
          </el-row>

          <el-row type="flex" justify="end">
             <el-button type="primary" icon="el-icon-search" size="mini" @click="handleSearch">{{ $t('common.search') }}</el-button>
             <el-button icon="el-icon-refresh-left" size="mini" @click="handleClear">{{ $t('common.clean') }}</el-button>
          </el-row>

        </el-form>
      </el-collapse-item>
    </el-collapse>
  </section>
</template>

<script>

import { mapGetters } from 'vuex';
import apiConstants from '@/utils/apiConstants';
import MasterRecordAutocomplete from '../components/autocomplete/MasterRecordAutocomplete';
import HubSelect from './components/hubSelect/HubSelect';

export default {
  name: 'SearchFilter',
  components: {
    MasterRecordAutocomplete,
    HubSelect
  },
  data() {
    return {
      isOpen: ['appointments-search-section'],
      accountUrl: apiConstants.END_POINTS.ACCOUNTS.ALL_BY_TYPE.replace('{accountTypeId}', 0),
      hubOptions: ['TX', 'OH', 'GA', 'FL', 'LH'],
      accountNameToEdit: null,
      filters: {
        accountId: null,
        loadNumber: '',
        hub: '',
        shipDateRange: [],
        appointmentDateRange: []
      }
    };
  },
  computed: {
    ...mapGetters(['appointments'])
  },
  methods: {
    onAccountText(text) {
      this.accountNameToEdit = text;
    },
    handleSearch() {
      // if user has typed something in the autocomplete but never selected a value,
      // filters.accountId will be null while accountNameToEdit contains text.  in
      // that case we should not fire the store action since the backend wouldn't
      // be able to resolve an account by name.
      if (this.accountNameToEdit && !this.filters.accountId) {
        return;
      }

      // eslint-disable-next-line no-unused-vars
      const query = {
        ...this.appointments.listQuery,
        page: 1,
        accountId: this.filters.accountId,
        loadNumber: this.filters.loadNumber,
        hub: this.filters.hub,
        shipDateFrom: this.filters.shipDateRange && this.filters.shipDateRange.length ? this.filters.shipDateRange[0] : null,
        shipDateTo: this.filters.shipDateRange && this.filters.shipDateRange.length ? this.filters.shipDateRange[1] : null,
        dateFrom: this.filters.appointmentDateRange && this.filters.appointmentDateRange.length ? this.filters.appointmentDateRange[0] : null,
        dateTo: this.filters.appointmentDateRange && this.filters.appointmentDateRange.length ? this.filters.appointmentDateRange[1] : null
      };
      this.$store.dispatch('appointments/setListQuery', query);
    },
    handleClear() {
      this.filters = {
        accountId: null,
        loadNumber: '',
        hub: '',
        shipDateRange: [],
        appointmentDateRange: []
      };
      this.accountNameToEdit = '';
      if (this.$refs.accountAuto && this.$refs.accountAuto.clearLocalModel) {
        this.$refs.accountAuto.clearLocalModel();
      }
      this.handleSearch();
    },
  }
};
</script>

<style scoped>
.responsive-filter-row {
  display: flex;
  flex-wrap: wrap;
  margin-left: -10px;
  margin-right: -10px;
}

.filter-col {
  padding-left: 10px;
  padding-right: 10px;
  box-sizing: border-box;
}

/*
 * CASO 1: Pantalla Grande (>= 901px)
 * Linea 1: Account, Load Number, Hub (33.3% cada uno)
 * Linea 2: Ship Date, Appt Date (50% cada uno)
 */
@media (min-width: 901px) {
  .account-col, .load-col, .hub-filter-col {
    width: 33.33333%;
    flex: 0 0 33.33333%;
    max-width: 33.33333%;
  }
  .ship-col, .appt-col {
    width: 50%;
    flex: 0 0 50%;
    max-width: 50%;
  }

  .account-col     { order: 1; }
  .load-col        { order: 2; }
  .hub-filter-col  { order: 3; }
  .ship-col        { order: 4; }
  .appt-col        { order: 5; }
}

/*
 * CASO 2: Pantalla Pequeña (< 900px)
 * Linea 1: Account, Load Number (50% cada uno)
 * Linea 2: Ship Date, Appt Date (50% cada uno)
 * Linea 3: Hub (100%)
 */
@media (max-width: 900px) {
  .account-col, .load-col {
    width: 50%;
    flex: 0 0 50%;
    max-width: 50%;
  }
  .ship-col, .appt-col {
    width: 50%;
    flex: 0 0 50%;
    max-width: 50%;
  }
  .hub-filter-col {
    /* Alineado a la izquierda (misma columna que Account y Ship Date) */
    width: 50%;
    flex: 0 0 50%;
    max-width: 50%;
  }

  .account-col    { order: 1; }
  .load-col       { order: 2; }
  .ship-col       { order: 3; }
  .appt-col       { order: 4; }
  .hub-filter-col { order: 5; }
}

/*
 * CASO 3: Pantalla Extra Grande (> 1360px)
 * Todos los campos en 1 sola fila alineados a la izquierda.
 */
@media (min-width: 1361px) {
  .account-col, .load-col, .hub-filter-col, .ship-col, .appt-col {
    /* Reseteamos para evitar conflictos */
    width: auto;
    max-width: none;
  }

  .account-col {
    width: 20%;
    flex: 0 0 20%;
    max-width: 20%;
    order: 1;
  }
  .load-col {
    width: 15%;
    flex: 0 0 15%;
    max-width: 15%;
    padding-right: 0; /* Ajuste opcional para compactar */
    order: 2;
  }
  .hub-filter-col {
    width: 15%;
    flex: 0 0 15%;
    max-width: 15%;
    order: 3;
    padding-right: 0;
  }
  .ship-col {
    width: 25%;
    flex: 0 0 25%;
    max-width: 25%;
    order: 4;
  }
  .appt-col {
    width: 25%;
    flex: 0 0 25%;
    max-width: 25%;
    order: 5;
  }

  /* Alineación de labels a la derecha y formato horizontal * /
  >>> .el-form-item {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  >>> .el-form-item__label {
    text-align: right;
    width: auto;
    padding-right: 5px;
    margin-bottom: 0;
    line-height: normal;
    white-space: nowrap;
  }

  >>> .el-form-item__content {
    flex-grow: 1;
    margin-left: 0 !important;
  }*/
}

/*
 * CASO INTERMEDIO: (901px - 1360px)
 * Row 1: Account, Ship Date, Appt Date
 * Row 2: Load Number, Hub
 */
@media (min-width: 901px) and (max-width: 1360px) {
  /* Row 1 */
  .account-col {
    width: 34%;
    flex: 0 0 34%;
    max-width: 34%;
    order: 1;
  }
  .ship-col {
    width: 33%;
    flex: 0 0 33%;
    max-width: 33%;
    order: 2;
  }
  .appt-col {
    width: 33%;
    flex: 0 0 33%;
    max-width: 33%;
    order: 3;
  }

  /* Row 2 */
  .load-col {
    width: 33%;
    flex: 0 0 33%;
    max-width: 33%;
    order: 4;
  }
  .hub-filter-col {
    width: 33%;
    flex: 0 0 33%;
    max-width: 33%;
    order: 5;
  }
}
</style>
